import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import config from '@iotv/config';
import { camelToDisplay, smashObjects } from '@iotv/datamodel-core';
import { Milliseconds } from '@iotv/iotv-v3-types';
import { Card, CardContent, Checkbox, FormControlLabel, FormGroup, Grid, Radio, RadioGroup, Table, TableBody, TableCell, TableRow } from '@mui/material';
import React, { useContext, useEffect, useRef } from 'react';
import { AppMainViewContext } from '../../../../ContextProviders/AppContextProvider';
import { AppLocalStorage } from '../../../../data/LocalStorage/AppLocalStorage';
import LineChart from '../../../../dataVisualization/Google/LineChart';
import { useContainerDimensions } from '../../../../hooks/useContainerDimenstions';
import { useMQTTChannelMessages } from '../../../../hooks/useMQTTChannelMessages';
import { getViewDefintionFromMutantZombie } from '../../../factories/AttributeDefinitions';
import { ObjectKeyPluckerTable } from '../../../generic/KeyPlucker/ObjectKeyPluckerTable';
import { SimpleIOModal } from '../../../io/VOBbased/SimpleIOModal';
import { IOType } from '../../../io/types';
import { NavSections } from '../../../navigation/NavSections';
import { TabPanel } from '../../../utils/TabPanelShim';
import { extractDiagnosticMessages, flatDiagnosticRecordsToTimestampedMultipleFARecords, flattenDeviceMessages, objectToChartProps, sortAndConvertByTimestamp, transformDiagnosticModeReport, } from './MonitorTabFns';
import { defaultSelectedKeys, MonitorMode, possibleKeys } from './types';
const debug = process.env.REACT_APP_DEBUG && false;
/** for use with utility at file:///./../../../../../../../lsc/Utilities/src/IoTCoreMQTT/v015DiagnosticMessages.ts */
const testXLSetTreeRequest = {
    nodeConfigs: [
        {
            axisId: 0,
            featureId: 0,
            conditionValue: 0,
            threshold: 0,
        },
    ],
    treeNodeCount: 1,
    treeNumber: 1,
    verify: 0x5a,
};
export const MonitorTab = ({ contextCustomer, userFunctions, value, index, thingMessages, deviceControlMessages, deviceConfig, selectedDevices, }) => {
    const [filterToSelected, setFilterToSelected] = React.useState(true);
    const [{ contextUser }] = useContext(AppMainViewContext);
    // const useStyles = makeStyles((theme) => ({ ...styles(theme) }));
    const widthRef = useRef(null);
    const { width, height } = useContainerDimensions(widthRef);
    // const localClasses = useStyles()
    const localClasses = {};
    const selectedDeviceSk = selectedDevices[0]?.sk;
    /** Replace both hooks with v2 for multiple devices */
    /** subscribe to device control responses topic */
    const deviceControlMessagesMqttTopic = selectedDeviceSk ? config.aws.mqtt.deviceControlResponseChannel.replace('[deviceSk]', selectedDeviceSk) : undefined;
    const { setterGetters: [diagnosticMessages, setDiagnosticMessages], } = useMQTTChannelMessages({
        user: contextUser,
        topic: deviceControlMessagesMqttTopic,
        objectFilter: (x) => !!x,
    });
    useEffect(() => {
        debug && console.log(`DeviceControlView used effect on diagnosticMessages`, diagnosticMessages[0]);
        if (diagnosticMessages.length > 0) {
            //   setDeviceControlMessages(receiveControlMessages);
        }
    }, [diagnosticMessages.length, selectedDeviceSk]);
    // debug && console.log('Monitor Tab has  deviceConfig ', deviceConfig)
    // debug && console.log('Monitor Tab has filter to selected', filterToSelected)
    debug &&
        console.log(`Monitor Tab got ${thingMessages?.length ?? 0} things messages and ${deviceControlMessages.length} device control messages and is looking for deviceIds`, selectedDevices.map((device) => device.sk.replace('Device:', '')));
    debug && (thingMessages?.length ?? 0) > 0 && console.log(`like this message`, thingMessages?.[0]);
    const filterDeviceMessages = (deviceMessage) => filterToSelected ? selectedDevices.find((device) => device.sk.includes(deviceMessage?.state?.reported?.meta?.deviceId ?? 'xxx')) : deviceMessage;
    const [showGraph, setShowGraph] = React.useState(true);
    const [monitorMode, setMonitorMode] = React.useState(AppLocalStorage.get(NavSections.DIAGNOSTIC, 'monitorTabMonitorMode') ?? MonitorMode.GENERAL_MESSAGES);
    const [objects, setObjects] = React.useState([]);
    useEffect(() => {
        const filteredMessages = thingMessages.filter(filterDeviceMessages);
        debug && console.log(`in monitoring mode ${monitorMode} filterDeviceMessages from ${thingMessages.length} unfiltered messages`, filteredMessages);
        switch (monitorMode) {
            case MonitorMode.GENERAL_MESSAGES:
                if (filteredMessages) {
                    const flattenedMessages = flattenDeviceMessages(filteredMessages);
                    debug && console.log('Setting objects to flattenedMessages', { filteredMessages, flattenedMessages });
                    setObjects(flattenedMessages ?? [{ nothing: 'is nothing' }]);
                }
                else {
                    debug && console.log(`not setting objects because filteredMessages is undefined but`, thingMessages);
                }
                break;
            case MonitorMode.DIAGNOSTIC_MESSAGES:
                {
                    const flattenedDiagnosticMessages = extractDiagnosticMessages(diagnosticMessages);
                    const defaultPeriodMs = flattenedDiagnosticMessages.length > 1
                        ? flattenedDiagnosticMessages.at(1)?.timestamp - flattenedDiagnosticMessages.at(0)?.timestamp
                        : Milliseconds.MINUTE * 2;
                    const downlinkXlSetTree = deviceConfig?.requested?.downlinkXlSetTree ?? testXLSetTreeRequest;
                    const separatedDiagnosticValuesMessages = [];
                    flattenedDiagnosticMessages.forEach((diagnosticMessage, i) => {
                        const lastTimestamp = flattenedDiagnosticMessages[i - 1]?.timestamp ?? (diagnosticMessage.timestamp ?? 0) - defaultPeriodMs;
                        const diagMsgSeparated = transformDiagnosticModeReport({ downlinkXlSetTree, diagnosticMessage, lastTimestamp });
                        debug && console.log('diagMsgSeparated', diagMsgSeparated);
                        diagMsgSeparated.forEach((msg) => separatedDiagnosticValuesMessages.push(msg));
                    });
                    const consolidatedMultpleFARecords = Object.values(flatDiagnosticRecordsToTimestampedMultipleFARecords(separatedDiagnosticValuesMessages));
                    debug &&
                        console.log('Setting objects to separatedDiagnosticValuesMessages', {
                            filteredMessages,
                            separatedDiagnosticValuesMessages,
                            consolidatedMultpleFARecords,
                        });
                    setObjects(consolidatedMultpleFARecords ?? [{ nothing: 'is nothing' }]);
                }
                break;
            default: {
                debug && console.log('Setting objects to filteredMessages', filteredMessages);
                setObjects(filteredMessages);
            }
        }
    }, [thingMessages?.length, deviceControlMessages?.length, diagnosticMessages.length, monitorMode, filterToSelected]);
    const [selectedKeys, setSelectedKeys] = React.useState(AppLocalStorage.get(NavSections.DIAGNOSTIC, 'userSelectedKeysMonitorTab') ?? defaultSelectedKeys);
    const handleCheckboxChange = (e) => {
        const checked = e.currentTarget.checked;
        const clone = [...selectedKeys];
        if (checked) {
            clone.push(e.currentTarget.value);
        }
        else {
            const idx = selectedKeys.findIndex((key) => key === e.currentTarget.value);
            if (idx) {
                clone.splice(idx, 1);
            }
        }
        AppLocalStorage.set(NavSections.DIAGNOSTIC, 'userSelectedKeysMonitorTab', clone);
        setSelectedKeys(clone);
    };
    const smashedObject = { ...smashObjects(objects), type: 'Fucktuple' };
    const exportViewDefinition = getViewDefintionFromMutantZombie(smashedObject, monitorMode === MonitorMode.GENERAL_MESSAGES ? selectedKeys : undefined);
    const chartProps = showGraph
        ? objectToChartProps(camelToDisplay(monitorMode.toLowerCase().replace('_', ' ')), objects.map((object) => Object.fromEntries(Object.entries(object).filter(([k, v]) => monitorMode === MonitorMode.DIAGNOSTIC_MESSAGES || k === 'timestamp' || selectedKeys.includes(k)))))
        : undefined;
    if (chartProps && width) {
        chartProps.options.width = width;
    }
    return (_jsx(TabPanel, { value, index, children: _jsxs(Grid, { container: true, spacing: 1, children: [_jsxs(Grid, { size: 12, children: [_jsx("div", { children: selectedDevices.length > 0
                                ? `Selected devices: ${selectedDevices.map((item) => {
                                    const vob = item;
                                    return vob.deviceId ?? vob.name ?? vob.eui;
                                })}`
                                : 'No devices selected!' }), _jsx(FormGroup, { row: true, children: _jsx(RadioGroup, { value: monitorMode, row: true, children: Object.entries(MonitorMode).map(([k, v]) => (_jsx(FormControlLabel, { value: k,
                                    label: camelToDisplay(v.toLowerCase().replace('_', ' ')),
                                    onChange: () => {
                                        setMonitorMode(v);
                                        AppLocalStorage.set(NavSections.DIAGNOSTIC, 'monitorTabMonitorMode', v);
                                    },
                                    control: _jsx(Radio, {}) }, k))) }) })] }, 'item1'), _jsx(Grid, { size: 12, children: _jsx(SimpleIOModal, { contextCustomer,
                        allowIOTypeToggle: false,
                        initialIOType: IOType.EXPORT,
                        setPlainObjectsInParent: () => { },
                        plainObjects: objects.map((ob) => ({ ...ob, type: 'TimeSeriesRecord' })) }, 'SimpleXPORT') }, 'item2'), monitorMode === MonitorMode.GENERAL_MESSAGES && (_jsx(Grid, { size: {
                        xs: 2,
                        lg: 2,
                    }, children: _jsx(Card, { children: _jsx(CardContent, { children: _jsx(Table, { children: _jsxs(TableBody, { children: [_jsxs(TableRow, { children: [_jsx(TableCell, { className: localClasses.MonitorTableCell, children: _jsx(Checkbox, { size: 'small',
                                                        value: 'all',
                                                        onChange: (e) => (e.target.checked ? setSelectedKeys(possibleKeys) : setSelectedKeys([])) }) }), _jsx(TableCell, { className: localClasses.MonitorTableCell, children: "All / None" })] }), possibleKeys.map((k, i) => (_jsxs(TableRow, { children: [_jsx(TableCell, { className: localClasses.MonitorTableCell, children: _jsx(Checkbox, { size: 'small', value: k, checked: selectedKeys.includes(k), onChange: handleCheckboxChange, name: `${i}_cb` }) }), _jsx(TableCell, { className: localClasses.MonitorTableCell, children: k })] }, k)))] }) }) }) }) }, 'item4')), _jsxs(Grid, { size: {
                        xs: 9,
                        lg: 10,
                    }, children: [chartProps && _jsx(LineChart, { ...chartProps, children: " " }), _jsx(ObjectKeyPluckerTable, { objects: sortAndConvertByTimestamp(objects),
                            keys: monitorMode === MonitorMode.GENERAL_MESSAGES && selectedKeys.length > 0 ? ['timestamp', ...selectedKeys] : undefined,
                            tableCellClassname: localClasses.MonitorTableCell })] }, 'item3')] }, 'cont') }));
};
